import { Controller, useForm } from "react-hook-form";
import InputWithHeader from "../../../components/Input/InputWithHeader";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useContext, useMemo } from "react";
import LoadingContext from "../../../contexts/LoadingContext";
import { useHistory } from "react-router";
import ToastContext from "../../../contexts/ToastContext";
import styles from "./bvn-verification.module.scss";
import padlock from "../../../assets/padlock.svg";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Stepper from "../components/stepper";
import Verified from "../../../assets/verified.svg";
import { STEPS } from "../constants";
import { verifyBvnSendOtp } from "../../../swr/mortgage";
import { VerifyBvnSendOtpRequest } from "../../../types/mortgage";
import { encodeBase64 } from "../../../utils/generalUtils";

const buildSchema = () =>
  yup.object({
    bvn: yup
      .string()
      .defined()
      .required()
      .length(11, "BVN should be 11 digits"),
    consentGiven: yup.boolean(),
  });

const MortgageBvnVerification = () => {
  const history = useHistory();
  const { openError } = useContext(ToastContext);
  const { setLoading } = useContext(LoadingContext);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<VerifyBvnSendOtpRequest>({
    resolver: yupResolver(buildSchema()) as any,
    defaultValues: {
      bvn: undefined,
      consentGiven: undefined,
    },
  });

  const bvn = watch("bvn");
  const consentGiven = watch("consentGiven");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue("consentGiven", event.target.checked);
  };

  const renderItemBelowInput = () => (
    <div className={styles.belowInput}>
      <img src={padlock} alt="secure" className={styles.padlock} />
      <span className={styles["small_72"]}>
        11-digit number linked to your registered phone number
      </span>
    </div>
  );

  const canSubmit = useMemo(
    () => (bvn && bvn?.length === 11 && consentGiven ? true : false),
    [consentGiven, bvn]
  );

  const onSubmit = async (req: VerifyBvnSendOtpRequest) => {
    setLoading(true);
    try {
      const { data, error } = await verifyBvnSendOtp(req);
      if (data) {
        // app - bvn
        history.push(
          `/bvn-otp-verification?app=${encodeBase64(
            req?.bvn
          )}&tac=${encodeBase64(data?.data?.maskedContact || "")}`
        );
      }
      if (error) {
        openError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "An error occurred"
        );
      }
    } catch (error) {
      openError("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.container}>
        <header className={styles.header}>
          <span className={styles.header_text}>Apply For Mortgage</span>
        </header>

        <Stepper steps={STEPS} currentStep={0} />

        <span className={styles.h4_38}>
          We'll run a quick verification on your Bank Verification Number (BVN).{" "}
        </span>

        <div className={styles.data_cont}>
          <div className={styles.flex_row}>
            <img src={Verified} alt="Verified" className={styles.verified} />
            <span className={styles.data_text_highlighted}>
              Your data is protected.
            </span>
          </div>
          <span className={styles.data_text}>
            BVN verification is conducted through a licensed credit bureau. No
            data is stored beyond this session.
          </span>
        </div>

        <div className={styles.grid}>
          <Controller
            name="bvn"
            control={control}
            render={({ field }) => (
              <InputWithHeader
                header="BVN"
                placeholder="Input BVN"
                type="string"
                value={field.value || ""}
                formKey="bvn"
                handleChange={(_, val) => field.onChange(val)}
                maxLength={11}
                error={errors.bvn?.message}
                renderItemBelowInput={renderItemBelowInput}
              />
            )}
          />
        </div>

        <FormGroup className={styles.consent_group}>
          <FormControlLabel
            required
            control={
              <Checkbox
                value={consentGiven}
                onChange={handleChange}
                sx={{
                  color: "#bfbfbf",
                  "&.Mui-checked": {
                    color: "#0d600d",
                  },
                }}
              />
            }
            label="By ticking this box, you consent to the verification of your BVN for identity validation. If you are not an Access Bank customer, you also authorize us to use your verified BVN information to pre-populate your account opening details where applicable. Your information will be processed securely and in accordance with applicable data protection laws"
            className={styles.consent_text}
          />
        </FormGroup>

        <button
          style={{
            backgroundColor: canSubmit
              ? "rgba(13, 96, 13, 1)"
              : "rgba(13, 96, 13, 0.5)",
          }}
          className={styles.button}
          disabled={!canSubmit}
        >
          Continue
        </button>
      </div>
    </form>
  );
};

export default MortgageBvnVerification;
